import i18n from "../i18n";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sprout, ArrowLeft, KeyRound } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function ResetPasswordPage() {
    const token = new URLSearchParams(useLocation().search).get("token") || "";
    const navigate = useNavigate();
    const { clearAuthState } = useAuth();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (event) => {
        event.preventDefault();
        setError("");
        if (password !== confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/auth/reset-password", { token, new_password: password });
            clearAuthState();
            setPassword("");
            setConfirm("");
            navigate("/login?passwordReset=success", { replace: true });
        } catch (requestError) {
            setError(formatApiErrorDetail(requestError.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-[#0f1f16] text-white grid place-items-center px-4 py-10">
            <section className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-7 sm:p-10 shadow-2xl" data-testid="reset-password-card">
                <div className="w-14 h-14 rounded-2xl bg-primary grid place-items-center mb-6"><Sprout className="w-7 h-7" /></div>
                <p className="tiny-label text-primary/80">{i18n.t("final.ea1787935b0b")}</p>
                <h1 className="text-3xl font-bold mt-2">{i18n.t("final.3f657f29e683")}</h1>
                <p className="text-sm text-white/60 mt-3">{i18n.t("final.f0f704fd3636")}</p>
                {!token && <div role="alert" className="mt-6 text-sm text-red-300 bg-red-500/10 border border-red-400/20 rounded-xl p-4">{i18n.t("final.57d6453a76e4")}</div>}
                <form onSubmit={submit} className="mt-8 space-y-5">
                    <div><Label htmlFor="new-password" className="tiny-label text-white/70">{i18n.t("final.d850ee188c7c")}</Label><Input id="new-password" type="password" required minLength={12} value={password} onChange={(event) => setPassword(event.target.value)} data-testid="password-reset-password-input" className="h-13 mt-2 bg-white/5 border-white/15 text-white" /></div>
                    <div><Label htmlFor="confirm-password" className="tiny-label text-white/70">{i18n.t("final.4a7c565d4c44")}</Label><Input id="confirm-password" type="password" required minLength={12} value={confirm} onChange={(event) => setConfirm(event.target.value)} data-testid="password-reset-confirm-input" className="h-13 mt-2 bg-white/5 border-white/15 text-white" /></div>
                    {message && <div data-testid="password-reset-message" role="status" className="text-sm text-emerald-200 bg-emerald-500/10 border border-emerald-400/20 rounded-xl p-4">{message}</div>}
                    {error && <div role="alert" className="text-sm text-red-300 bg-red-500/10 border border-red-400/20 rounded-xl p-4">{error}</div>}
                    <Button type="submit" disabled={loading || !token || Boolean(message)} data-testid="password-reset-submit-button" className="w-full h-13 rounded-2xl"><KeyRound className="w-4 h-4 mr-2" />{loading ? "Resetting…" : "Reset password"}</Button>
                </form>
                <Link to="/login" className="mt-7 inline-flex items-center text-sm text-primary font-semibold"><ArrowLeft className="w-4 h-4 mr-2" />{i18n.t("final.4da821676b2f")}</Link>
            </section>
        </main>
    );
}
